import clsx from "clsx";

import { formatting } from "@north-beam/nb-common";
import { MetricValue } from "@shared/metric-value";

import {
  type BenchmarkIndicatorRow,
  type UniversalBenchmarksStage,
} from "./types";
import { isIndicatorRowsOfStage, isPositiveChange } from "./utils";

interface Props {
  rows: BenchmarkIndicatorRow[];
  preferredStage?: UniversalBenchmarksStage;
}

export const UniversalBenchmarksTable = ({ rows, preferredStage }: Props) => {
  return (
    <div className="border border-v2-tertiary rounded bg-v2-primary">
      <table className="w-full">
        <thead>
          <tr className="text-xs font-bold border-b border-v2-secondary">
            <th className="h-8 px-4 border-r border-v2-tertiary">KPI</th>
            <th className="h-8">
              <div className="flex items-center justify-end h-full">
                <div className="px-4">CURRENT</div>
                <div className="w-[1px] h-1/2 border-r border-v2-tertiary"></div>
              </div>
            </th>
            <th className="h-8 px-4 text-right">TARGET</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const shouldHighlight =
              preferredStage && isIndicatorRowsOfStage(row, preferredStage);

            const change = Math.abs(1 - row.current / row.target);
            const formattedChange = formatting.formatPercentage(change, {
              mantissa: 1,
            });

            return (
              <tr
                key={row.name}
                className={clsx("h-8 text-xs", {
                  "border-b border-v2-tertiary": index < rows.length - 1,
                  "bg-v2-highlight": shouldHighlight,
                })}
              >
                <td className="text-xs px-4 border-r border-v2-tertiary">
                  <div className="flex items-center justify-between gap-4">
                    {row.name}
                    <div
                      className={clsx(
                        "font-mono text-xs rounded h-5 flex items-center gap-1 px-1.5",
                        "text-nowrap",
                        {
                          "bg-v2-highlight-notice text-v2-action-positive":
                            isPositiveChange(row),
                          "bg-v2-mars/400/10% text-v2-action-negative":
                            !isPositiveChange(row),
                        },
                      )}
                    >
                      <span>{formattedChange}</span>
                      {row.current !== row.target && (
                        <i
                          className={clsx("fa-solid", {
                            "fa-arrow-up": row.current > row.target,
                            "fa-arrow-down": row.current < row.target,
                          })}
                        />
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 text-right">
                  <MetricValue value={row.current} format={row.format} />
                </td>
                <td className="px-4 text-right">
                  <MetricValue value={row.target} format={row.format} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
